// react-big-calendar lays events out with browser-local Date components. A
// facility has its own IANA timezone, so the calendar works in "wall-clock"
// Dates: a Date whose LOCAL components equal the facility's wall clock. These
// helpers convert real instants to wall-clock Dates and back.

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

function partsAt(instant: Date, timeZone: string): WallParts | null {
  if (Number.isNaN(instant.getTime())) return null;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = formatterFor(timeZone).formatToParts(instant);
  } catch {
    return null;
  }
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const result = {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour") % 24,
    minute: read("minute"),
  };
  return Object.values(result).every(Number.isInteger) ? result : null;
}

/** The facility wall clock of `instant` as a browser-local Date. */
export function toWallClock(instant: Date, timeZone: string): Date {
  const parts = partsAt(instant, timeZone);
  if (!parts) return new Date(Number.NaN);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
}

/**
 * The real instant for a facility wall-clock Date. A wall time skipped by a
 * spring-forward change resolves to the instant just after the gap.
 */
export function fromWallClock(wall: Date, timeZone: string): Date {
  if (Number.isNaN(wall.getTime())) return new Date(Number.NaN);
  const target = Date.UTC(
    wall.getFullYear(),
    wall.getMonth(),
    wall.getDate(),
    wall.getHours(),
    wall.getMinutes(),
  );
  let guess = target;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = partsAt(new Date(guess), timeZone);
    if (!parts) return new Date(Number.NaN);
    const seen = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
    );
    const delta = target - seen;
    if (delta === 0) return new Date(guess);
    guess += delta;
  }
  return new Date(guess);
}

/** The facility-local calendar day of `instant` as YYYY-MM-DD. */
export function zonedDayKey(instant: Date, timeZone: string): string {
  const parts = partsAt(instant, timeZone);
  if (!parts) return "";
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

// Wall-clock Dates carry the facility's wall time in browser-local fields, so
// arithmetic on their epoch value inherits the BROWSER's DST jumps. These
// helpers do the arithmetic on the wall fields themselves: an hour of length
// is an hour on the facility's clock, whatever the browser's zone does.
function wallStamp(wall: Date): number {
  return Date.UTC(
    wall.getFullYear(),
    wall.getMonth(),
    wall.getDate(),
    wall.getHours(),
    wall.getMinutes(),
  );
}

/** `wall` plus `minutes` of facility wall-clock time. */
export function addWallMinutes(wall: Date, minutes: number): Date {
  const shifted = new Date(wallStamp(wall) + minutes * 60_000);
  return new Date(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    shifted.getUTCHours(),
    shifted.getUTCMinutes(),
  );
}

/** Wall-clock minutes from `start` to `end`. */
export function wallMinutesBetween(start: Date, end: Date): number {
  return Math.round((wallStamp(end) - wallStamp(start)) / 60_000);
}
